import { consoleLog } from "../consoleLog/consoleLog";

/**
 * The idempotency key that pairs an attempt's two calls — the one that starts it
 * and the one that confirms it after payment.
 *
 * ── The rule ────────────────────────────────────────────────────────────────
 * The key is MINTED BY THE BACKEND, not here. The opening call
 * (`/itinerary/prebooking`, `/trips/hotels/rebook`) generates a uuid, sends it
 * to the booking engine (header + body), and returns it on its response. This
 * module's only job is to remember that value so the confirm can send it back —
 * where app-backend turns it into the `Idempotency-Key` header on the engine
 * call.
 *
 * Because the backend mints one per opening CALL, starting again — a changed
 * cart, a retry, a second attempt after a failure — replaces the remembered key,
 * and the confirm that follows carries the NEW one. The two calls of a single
 * attempt always agree; two attempts never share a key.
 *
 * ── Why scopes ──────────────────────────────────────────────────────────────
 * A new booking and a post-booking hotel rebook are separate attempts with
 * separate confirms, and both round-trip through Stripe. One shared slot would
 * let a rebook overwrite the key a pending booking confirm still needs — and,
 * worse, let a confirm pair itself with the wrong attempt. Each flow gets its
 * own slot.
 *
 * ── Why sessionStorage ──────────────────────────────────────────────────────
 * The confirm happens after the Stripe redirect, i.e. a different page load, so
 * the key has to outlive the page it was minted on — sessionStorage survives
 * that within the tab. Deliberately NOT localStorage: that is shared across
 * tabs, so two attempts in two tabs would overwrite each other's key and could
 * be collapsed by the engine into ONE booking. Losing the key when a payment
 * returns in a brand-new tab is the far cheaper failure — the confirm then
 * carries no key, exactly as it did before this existed.
 *
 * Never throws: storage can be unavailable (private mode, blocked site data),
 * and a booking must not fail for want of a dedupe hint.
 */
const STORAGE_KEYS = {
  booking: "_zenvoya_booking_idempotency_key",
  hotelRebook: "_zenvoya_hotel_rebook_idempotency_key",
} as const;

type IdempotencyScope = keyof typeof STORAGE_KEYS;

/**
 * Remember the key the opening call's response carried, for its confirm.
 *
 * A missing/blank value CLEARS the stored one rather than leaving it: a call
 * that returned no key (an older backend, or a response shape we did not
 * expect) must not let the confirm pair itself with the PREVIOUS attempt's key
 * — sending no key is correct there, sending a stale one is not.
 */
const remember = (scope: IdempotencyScope, value: unknown): void => {
  const key = typeof value === "string" ? value.trim() : "";
  try {
    if (key) sessionStorage.setItem(STORAGE_KEYS[scope], key);
    else sessionStorage.removeItem(STORAGE_KEYS[scope]);
  } catch {
    consoleLog(["IDEMPOTENCY KEY: storage unavailable; the confirm will send none", scope]);
  }
};

/**
 * The remembered key as a spreadable body field — so the call site reads like
 * the other optional body fields (`getUserTimezoneField`,
 * `getRequestedItineraryIdField`).
 *
 * `{}` when there is none: a confirm with no opening call behind it in this tab
 * (a new tab after the Stripe redirect, storage unavailable, or a backend that
 * has not shipped the minting half). The field is simply omitted then, which is
 * the pre-existing behaviour.
 */
const field = (scope: IdempotencyScope): { idempotencyKey?: string } => {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEYS[scope]);
    return stored ? { idempotencyKey: stored } : {};
  } catch {
    return {};
  }
};

/** New booking: `/itinerary/prebooking` → `/itinerary/booking`. */
export const rememberBookingIdempotencyKey = (value: unknown): void => remember("booking", value);
export const getBookingIdempotencyKeyField = (): { idempotencyKey?: string } => field("booking");

/** Post-booking hotel modify: `/trips/hotels/rebook` → `/trips/hotels/rebook/confirm`. */
export const rememberHotelRebookIdempotencyKey = (value: unknown): void => remember("hotelRebook", value);
export const getHotelRebookIdempotencyKeyField = (): { idempotencyKey?: string } => field("hotelRebook");
